// Every routed page, loaded on demand (one chunk each; shared code lands in shared chunks).
import { lazyPage } from './lazyPage';

/** Chunk loaders, also used for idle prefetching. */
export const load = {
  login: () => import('@features/auth/pages/LoginPage').then((m) => m.LoginPage),
  enrollGate: () => import('@features/auth/pages/EnrollGatePage').then((m) => m.EnrollGatePage),
  print: () => import('@features/shipments/pages/ShipmentPrintPage').then((m) => m.ShipmentPrintPage),
  cylinderLabels: () => import('@features/cylinderLabels/pages/CylinderLabelsPage').then((m) => m.CylinderLabelsPage),
  verify: () => import('@features/shipments/pages/ShipmentVerifyPage').then((m) => m.ShipmentVerifyPage),
  acceptInvite: () => import('@features/auth/pages/AcceptInvitePage').then((m) => m.AcceptInvitePage),
  // public Hub registration: its own chunks, nothing of the staff app in them
  hubLanding: () => import('@features/hubRegistration/pages/HubLandingPage').then((m) => m.HubLandingPage),
  hubForm: () => import('@features/hubRegistration/pages/HubFormPage').then((m) => m.HubFormPage),
  hubStatus: () => import('@features/hubRegistration/pages/HubStatusPage').then((m) => m.HubStatusPage),
  hubIntegrity: () => import('@features/hubRegistration/pages/HubIntegrityTemplatePage').then((m) => m.HubIntegrityTemplatePage),
  hubApplications: () => import('@features/hubApplications/pages/HubApplicationsPage').then((m) => m.HubApplicationsPage),
  hubApplication: () => import('@features/hubApplications/pages/HubApplicationDetailPage').then((m) => m.HubApplicationDetailPage),
  hubVisits: () => import('@features/hubVisits/pages/HubVisitsPage').then((m) => m.HubVisitsPage),
  hubVisit: () => import('@features/hubVisits/pages/HubVisitPage').then((m) => m.HubVisitPage),
  deliveries: () => import('@features/courier/pages/DeliveriesPage').then((m) => m.DeliveriesPage),
  delivery: () => import('@features/courier/pages/DeliveryPage').then((m) => m.DeliveryPage),
  households: () => import('@features/households/pages/HouseholdsPage').then((m) => m.HouseholdsPage),
  household: () => import('@features/households/pages/HouseholdDetailPage').then((m) => m.HouseholdDetailPage),
  householdsImport: () => import('@features/households/pages/HouseholdImportPage').then((m) => m.HouseholdImportPage),
  orders: () => import('@features/orders/pages/OrdersPage').then((m) => m.OrdersPage),
  order: () => import('@features/orders/pages/OrderDetailPage').then((m) => m.OrderDetailPage),
  programSettings: () => import('@features/programSettings/pages/ProgramSettingsPage').then((m) => m.ProgramSettingsPage),
  overview: () => import('@features/overview/pages/OverviewPage').then((m) => m.OverviewPage),
  home: () => import('@features/home/pages/HomePage').then((m) => m.HomePage),
  statistics: () => import('@features/statistics/pages/StatisticsPage').then((m) => m.StatisticsPage),
  trace: () => import('@features/trace/pages/TracePage').then((m) => m.TracePage),
  shipments: () => import('@features/shipments/pages/ShipmentsPage').then((m) => m.ShipmentsPage),
  shipment: () => import('@features/shipments/pages/ShipmentDetailPage').then((m) => m.ShipmentDetailPage),
  cylinders: () => import('@features/cylinders/pages/CylindersPage').then((m) => m.CylindersPage),
  cylinder: () => import('@features/cylinders/pages/CylinderDetailPage').then((m) => m.CylinderDetailPage),
  cylinderByEpc: () => import('@features/cylinders/pages/CylinderByEpcPage').then((m) => m.CylinderByEpcPage),
  locations: () => import('@features/locations/pages/LocationsPage').then((m) => m.LocationsPage),
  location: () => import('@features/locations/pages/LocationDetailPage').then((m) => m.LocationDetailPage),
  gateways: () => import('@features/gateways/pages/GatewaysPage').then((m) => m.GatewaysPage),
  gateway: () => import('@features/gateways/pages/GatewayDetailPage').then((m) => m.GatewayDetailPage),
  alerts: () => import('@features/alerts/pages/AlertsPage').then((m) => m.AlertsPage),
  account: () => import('@features/account/pages/AccountPage').then((m) => m.AccountPage),
  updates: () => import('@features/updates/pages/UpdatesPage').then((m) => m.UpdatesPage),
  users: () => import('@features/users/pages/UsersPage').then((m) => m.UsersPage),
  audit: () => import('@features/audit/pages/AuditPage').then((m) => m.AuditPage),
  operations: () => import('@features/operations/shell/ui/OperationsLayout').then((m) => m.OperationsLayout),
  operationsHome: () => import('@features/operations/home/pages/HomePage').then((m) => m.HomePage),
  dispatchSetup: () => import('@features/operations/dispatch/pages/DispatchSetupPage').then((m) => m.DispatchSetupPage),
  dispatch: () => import('@features/operations/dispatch/pages/DispatchPage').then((m) => m.DispatchPage),
  receiveList: () => import('@features/operations/receive/pages/ReceiveListPage').then((m) => m.ReceiveListPage),
  receive: () => import('@features/operations/receive/pages/ReceivePage').then((m) => m.ReceivePage),
  register: () => import('@features/operations/register/pages/RegisterPage').then((m) => m.RegisterPage),
  lookup: () => import('@features/operations/lookup/pages/LookupPage').then((m) => m.LookupPage),
  test: () => import('@features/operations/test/pages/TestPage').then((m) => m.TestPage),
  hubOrders: () => import('@features/operations/orders/pages/HubOrdersPage').then((m) => m.HubOrdersPage),
  hubOrder: () => import('@features/operations/orders/pages/HubOrderPage').then((m) => m.HubOrderPage),
  hubHouseholds: () => import('@features/operations/households/pages/HubHouseholdsPage').then((m) => m.HubHouseholdsPage),
  hubHousehold: () => import('@features/operations/households/pages/HubHouseholdPage').then((m) => m.HubHouseholdPage),
  hubCounter: () => import('@features/operations/counter/pages/CounterPage').then((m) => m.CounterPage),
  hubCouriers: () => import('@features/operations/couriers/pages/CouriersPage').then((m) => m.CouriersPage),
  hubStockCount: () => import('@features/operations/stockCount/pages/StockCountPage').then((m) => m.StockCountPage),
};

export const LoginPage = lazyPage(load.login);
export const EnrollGatePage = lazyPage(load.enrollGate);
export const ShipmentPrintPage = lazyPage(load.print);
export const CylinderLabelsPage = lazyPage(load.cylinderLabels);
export const ShipmentVerifyPage = lazyPage(load.verify);
export const AcceptInvitePage = lazyPage(load.acceptInvite);
export const HubLandingPage = lazyPage(load.hubLanding);
export const HubFormPage = lazyPage(load.hubForm);
export const HubStatusPage = lazyPage(load.hubStatus);
export const HubIntegrityTemplatePage = lazyPage(load.hubIntegrity);
export const HubApplicationsPage = lazyPage(load.hubApplications);
export const HubApplicationDetailPage = lazyPage(load.hubApplication);
export const HubVisitsPage = lazyPage(load.hubVisits);
export const HubVisitPage = lazyPage(load.hubVisit);
export const DeliveriesPage = lazyPage(load.deliveries);
export const DeliveryPage = lazyPage(load.delivery);
export const HouseholdsPage = lazyPage(load.households);
export const HouseholdDetailPage = lazyPage(load.household);
export const HouseholdImportPage = lazyPage(load.householdsImport);
export const OrdersPage = lazyPage(load.orders);
export const OrderDetailPage = lazyPage(load.order);
export const ProgramSettingsPage = lazyPage(load.programSettings);
export const OverviewPage = lazyPage(load.overview);
export const HomePage = lazyPage(load.home);
export const HubCouriersPage = lazyPage(load.hubCouriers);
export const HubStockCountPage = lazyPage(load.hubStockCount);
export const StatisticsPage = lazyPage(load.statistics);
export const TracePage = lazyPage(load.trace);
export const ShipmentsPage = lazyPage(load.shipments);
export const ShipmentDetailPage = lazyPage(load.shipment);
export const CylindersPage = lazyPage(load.cylinders);
export const CylinderDetailPage = lazyPage(load.cylinder);
export const CylinderByEpcPage = lazyPage(load.cylinderByEpc);
export const LocationsPage = lazyPage(load.locations);
export const LocationDetailPage = lazyPage(load.location);
export const GatewaysPage = lazyPage(load.gateways);
export const GatewayDetailPage = lazyPage(load.gateway);
export const AlertsPage = lazyPage(load.alerts);
export const AccountPage = lazyPage(load.account);
export const UpdatesPage = lazyPage(load.updates);
export const UsersPage = lazyPage(load.users);
export const AuditPage = lazyPage(load.audit);
export const OperationsLayout = lazyPage(load.operations);
export const OperationsHomePage = lazyPage(load.operationsHome);
export const DispatchSetupPage = lazyPage(load.dispatchSetup);
export const DispatchPage = lazyPage(load.dispatch);
export const ReceiveListPage = lazyPage(load.receiveList);
export const ReceivePage = lazyPage(load.receive);
export const RegisterPage = lazyPage(load.register);
export const LookupPage = lazyPage(load.lookup);
export const TestPage = lazyPage(load.test);
export const HubOrdersPage = lazyPage(load.hubOrders);
export const HubOrderPage = lazyPage(load.hubOrder);
export const HubHouseholdsPage = lazyPage(load.hubHouseholds);
export const HubHouseholdPage = lazyPage(load.hubHousehold);
export const HubCounterPage = lazyPage(load.hubCounter);

/** What each area prefetches once idle. */
export const SHELL_CHUNKS = [load.home, load.overview, load.shipments, load.shipment, load.cylinders, load.cylinder, load.locations, load.location, load.gateways, load.gateway, load.alerts, load.account];
export const ADMIN_CHUNKS = [load.updates, load.users, load.audit];
/** Pendaftaran Hub (hub_application.read) and Kunjungan (field verifiers), prefetched only for those who see them. */
export const HUB_CHUNKS = [load.hubApplications, load.hubApplication];
export const VISIT_CHUNKS = [load.hubVisits, load.hubVisit];
/** Warga, Pesanan and Pengaturan program (admins and readers), prefetched for those who see them. */
export const PROGRAM_CHUNKS = [load.households, load.household, load.orders, load.order];
/** Pengantaran (couriers): prefetched for them only (a delivery opens offline-ready). */
export const COURIER_CHUNKS = [load.deliveries, load.delivery];
export const OPERATIONS_CHUNKS = [load.operationsHome, load.dispatchSetup, load.dispatch, load.receiveList, load.receive, load.register, load.lookup, load.test, load.hubOrders, load.hubOrder, load.hubHouseholds, load.hubHousehold, load.hubCounter];
